import { createLogger } from '../utils/logger';
import {
  ServiceHttpError,
  errorEnvelopeSchema,
  parseErrorResponse, detailsForLog,
  isSessionExpiredCode,
  type ServiceErrorCode,
  type KnownServiceErrorCode,
} from './errorEnvelope';
import {
  sleep,
  withJitter,
  headersToObject,
  parseRetryAfterSeconds,
  attachRetryAfterSeconds, getActorEmailFromGlobal,
} from './httpHelpers';
import { attemptReHandoff } from '../auth/authReHandoff';
import { createLinkedAbortHandle } from './httpAbort';
import { encodeBody } from './serviceLayerClient.body';
import {
  QueryCancelledError,
  installCancellationRejectionGuard,
} from './slErrors';
import { dispatchUnauthorized } from './serviceLayerClient.auth';
import { buildRequestHeaders, rebuildHeadersAfterHandoff } from './serviceLayerClient.headers';
import type {
  RequestContext,
  RetryOptions,
  RateLimitInfo,
  ServiceFetchResult,
} from './serviceLayerClient.types';

// Re-exports preserve the public API after the errorEnvelope extraction and
// the serviceLayerClient.* sibling-module split (LOC ceiling).
export { ServiceHttpError, errorEnvelopeSchema };
export type { ServiceErrorCode, KnownServiceErrorCode };
export { getSupabaseAccessTokenFromStorage } from './serviceLayerClient.auth';
export type {
  RequestContext,
  RetryOptions,
  RateLimitInfo,
  ServiceFetchResult,
} from './serviceLayerClient.types';

// Caller-cancellation (React-Query observer unmount / route change / refetch
// superseding) aborts the in-flight fetch. When the cancelled request's
// promise has no awaiter (Promise.all siblings, un-awaited prefetch, persisted
// broadcast), its rejection would otherwise log "Uncaught (in promise)
// AbortError" — 56→127/session (BUG-FE-WIRE-UNCAUGHT-ABORTERROR-STORM-
// 2026-06-07). serviceFetch converts those into a typed QueryCancelledError;
// this guard consumes exactly those at the window level so they are HANDLED,
// never surfacing as unhandledrejection. Timeouts stay loud. Idempotent.
installCancellationRejectionGuard();

const env = import.meta.env;
// Use same-origin proxy path to avoid CORS in production; ops must proxy
// "/service" → https://api.clanda.de at the web server/CDN edge.
const BASE_URL: string = env.VITE_SERVICE_LAYER_BASE_URL || '/service';
const DEBUG_HTTP: boolean = env.VITE_DEBUG_HTTP === '1';
const TEST_BYPASS: boolean = env.VITE_SERVICE_TEST_BYPASS === '1';
const TEST_ACTOR_ENV: string | undefined = env.VITE_SERVICE_TEST_ACTOR;
const HMAC_SECRET: string | undefined = env.VITE_SERVICE_LAYER_SECRET_K1;
const HMAC_ACTOR: string | undefined = env.VITE_SERVICE_LAYER_ACTOR;
// Enable debug auth logging if URL param is present or env var is set
const ENABLE_AUTH_DEBUG = typeof window !== 'undefined' 
  ? (new URLSearchParams(window.location.search).get('debug_auth') === '1' || env.VITE_DEBUG_AUTH === '1')
  : false;

const httpLogger = createLogger('service-client');
// Allow-list of consumer-side auto-handled error envelopes — logged `warn` not `error` (error still throws). 409 SESSION_ALREADY_ACTIVE → useCallingSession.ts auto-adopts.
const QUIET_ERROR_LOG_PAIRS: ReadonlyArray<readonly [number, string]> = [[409, 'SESSION_ALREADY_ACTIVE']];
const AUTH_PATH_PREFIX = '/v1/auth/'; // cookie-handoff, session: a 401 there never re-hands-off (no loop)

// JWT read / staleness / unauthorized dispatch live in serviceLayerClient.auth
// (LOC ceiling); getSupabaseAccessTokenFromStorage is re-exported above for the
// public API. dispatchUnauthorized is consumed below on an authoritative 401.
//
// Session-expiry trigger set is the single SSoT in errorEnvelope.ts
// (SESSION_EXPIRED_CODES, cross-checked against KNOWN_ERROR_CODES by
// errorEnvelope.session.test.ts). Consumed here via `isSessionExpiredCode`
// so the overlay's trigger value-set can NEVER silently diverge from the
// FE↔SL error taxonomy. Runtime dispatch below is unchanged: still only a
// clean 401 OR an explicit session-expiry code drives the overlay; 403
// FORBIDDEN deliberately does not (the 2026-06-01 all-dark regression #1793).

// Env flags + secrets the header builder needs, read once here so
// serviceLayerClient.ts stays the single `import.meta.env` site.
const HEADER_ENV = {
  DEBUG_HTTP,
  ENABLE_AUTH_DEBUG,
  TEST_BYPASS,
  TEST_ACTOR_ENV,
  HMAC_SECRET,
  HMAC_ACTOR,
};

export async function serviceFetch<T>(
  path: string,
  init: RequestInit & { ctx?: RequestContext; retry?: RetryOptions; timeoutMs?: number } = {}
): Promise<ServiceFetchResult<T>> {
  const method = (init.method || 'GET').toUpperCase();
  const retry: RetryOptions = init.retry || {};
  const maxAttempts = Math.max(1, Math.min(5, retry.maxAttempts ?? 2));

  // Header assembly (Content-Type, request-id, actor/org, HMAC, impersonation, Bearer-JWT iOS-Safari
  // fallback, CSRF, test bypass) lives in serviceLayerClient.headers (LOC ceiling).
  const built = await buildRequestHeaders(path, method, init, HEADER_ENV);
  const initialRequestId = built.requestId;
  let baseHeaders = built.headers; // rebuilt once after a successful 401 handoff (fresh CSRF + Bearer)

  // Link caller signal + timeoutMs into one handle (see httpAbort.ts).
  const linkedAbort = createLinkedAbortHandle({
    timeoutMs: init.timeoutMs,
    callerSignal: (init as RequestInit).signal ?? undefined,
  });

  let lastError: ServiceHttpError | null = null;
  let attempt = 0;
  let reHandoffTried = false; // at most ONE re-handoff per call: the re-send never refreshes again
  try {
    // Retry loop
    // eslint-disable-next-line no-constant-condition
    while (true) {
      attempt += 1;
      const start = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
      const url = `${BASE_URL}${path}`;
      // Ensure Authorization header is included in fetch options
      // Some browsers may strip Authorization from headers object, so we set it explicitly
      // Stringify body if it's an object (for JSON requests)
      let bodyToSend: BodyInit | undefined = init.body ?? undefined;
      if (bodyToSend && typeof bodyToSend === 'object' && !(bodyToSend instanceof FormData) && !(bodyToSend instanceof Blob)) {
        bodyToSend = JSON.stringify(bodyToSend);
      }
      
      const fetchOptions: RequestInit = {
        ...init,
        body: bodyToSend,
        headers: baseHeaders,
        signal: linkedAbort.signal,
        // Include cookies for authentication (HttpOnly cookies set by Service Layer)
        credentials: 'include',
      };
      const res = await fetch(url, fetchOptions);
      const elapsed = Math.round(((typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()) - start) as number);

      // Safe debug log
      if (DEBUG_HTTP) {
        try {
          const u = new URL(url);
          const safePath = `${u.origin}${u.pathname}`;
          httpLogger.info(
            `[service-client] ${method} ${safePath} -> ${res.status} in ${elapsed}ms (attempt ${attempt}/${maxAttempts})`
          );
        } catch { /* noop */ }
      }

      const headersObj = headersToObject(res.headers);
      const rateLimit: RateLimitInfo = {
        limit: headersObj['x-ratelimit-limit'] != null ? Number(headersObj['x-ratelimit-limit']) : undefined,
        remaining: headersObj['x-ratelimit-remaining'] != null ? Number(headersObj['x-ratelimit-remaining']) : undefined,
        reset: headersObj['x-ratelimit-reset'] != null ? Number(headersObj['x-ratelimit-reset']) : undefined,
        retryAfter: parseRetryAfterSeconds(headersObj['retry-after']),
      };

      // Token refresh is handled server-side via HttpOnly cookies
      // No need to persist tokens from response headers

      const echoedRequestId = res.headers.get('X-Request-Id');
      const effectiveRequestId = echoedRequestId || initialRequestId;
      const text = await res.text();
      const contentType = res.headers.get('content-type') || '';
      const isJson = contentType.includes('application/json');
      const body = isJson && text ? JSON.parse(text) : text;

      if (!res.ok) {
        const { code, message, details } = parseErrorResponse(body, res.status, isJson);

        // Log auth errors concisely (details available in thrown ServiceHttpError)
        if (res.status === 401) {
          console.error(`[service-client] 401 UNAUTHORIZED: ${method} ${path} (request ${effectiveRequestId})`);
        } else if (res.status === 403) {
          console.error(`[service-client] 403 FORBIDDEN: ${method} ${path} (request ${effectiveRequestId})`);
        }

        // REFRESH ONCE, BEFORE THE OVERLAY (FE-V8 AC 13): ok → ONE re-send, headers rebuilt (rotated csrf_token), same
        // request id; else / 2nd 401 → overlay; never /v1/auth/*. Caller abort → no re-send, no overlay (cancel/TIMEOUT).
        if (res.status === 401 && !reHandoffTried && !path.startsWith(AUTH_PATH_PREFIX)) {
          reHandoffTried = true;
          const handed = await attemptReHandoff({ hadEmail: getActorEmailFromGlobal() !== null, signal: linkedAbort.signal });
          if (linkedAbort.signal?.aborted) throw new DOMException('Aborted during the 401 re-handoff', 'AbortError');
          if (handed) {
            baseHeaders = await rebuildHeadersAfterHandoff(path, method, init, HEADER_ENV, initialRequestId);
            continue;
          }
        }

        // Drive the global re-login overlay ONLY on an authoritative session
        // expiry: a clean 401, or an explicit session-expiry envelope code
        // (TOKEN_EXPIRED / SESSION_EXPIRED) on any status. A 403 FORBIDDEN is
        // authorization denial on a VALID session — it must surface as the
        // per-surface error thrown below, NOT blank the whole app behind the
        // overlay (the 2026-06-01 all-dark regression, #1793, where every 403
        // re-logged the operator out). The overlay is idempotent. Guarded so it
        // can NEVER mask the HTTP error.
        try {
          if (res.status === 401 || isSessionExpiredCode(code)) {
            dispatchUnauthorized({ path, method, requestId: effectiveRequestId, status: res.status, code });
          }
        } catch { /* event dispatch must never mask the HTTP error */ }

        const err = new ServiceHttpError(code, message, details, effectiveRequestId || undefined, res.status);
        if (rateLimit.retryAfter !== undefined) attachRetryAfterSeconds(err, rateLimit.retryAfter); // the outbox's floor
        lastError = err;

        // Log full error context for Sentry breadcrumbs (warn for quiet pairs).
        try {
          const log = QUIET_ERROR_LOG_PAIRS.some(([s, c]) => s === res.status && c === code) ? httpLogger.warn : httpLogger.error;
          log(`${method} ${path} → ${res.status} ${code}: ${message}`, {
            requestId: effectiveRequestId, status: res.status, code, details: detailsForLog(details), attempt,
          });
        } catch { /* logging must never mask the real error */ }

        // Retry on 429/5xx if attempts left
        if ((res.status === 429 || (res.status >= 500 && res.status <= 599)) && attempt < maxAttempts) {
          const retryAfterMs = rateLimit.retryAfter != null ? Math.max(0, rateLimit.retryAfter * 1000) : withJitter(500 * attempt);
          await sleep(retryAfterMs);
          continue;
        }

        throw err;
      }

      linkedAbort.cleanup();
      return { data: body as T, status: res.status, headers: headersObj, requestId: effectiveRequestId, rateLimit };
    }
  } catch (e: unknown) {
    linkedAbort.cleanup();
    if (e instanceof DOMException && e.name === 'AbortError') {
      // Two abort sources, two behaviours (BUG-FE-WIRE-UNCAUGHT-ABORTERROR-STORM-2026-06-07):
      //   (a) OUR timeout fired → a real server-too-slow failure → typed ServiceHttpError('TIMEOUT'), loud (D-33).
      //   (b) the CALLER's signal aborted (React-Query cancel: unmount / route change / superseding refetch) → NOT a
      //       failure. A raw DOMException surfaced as "Uncaught (in promise) AbortError" wherever nothing awaited it;
      //       a typed QueryCancelledError is consumed as HANDLED by installCancellationRejectionGuard (above), and
      //       React Query's retryer discards it exactly like the old AbortError (queryClient matches it by name).
      if (linkedAbort.timedOut()) {
        throw new ServiceHttpError(
          'TIMEOUT',
          `Request timed out after ${init.timeoutMs}ms`,
          { path, method, timeoutMs: init.timeoutMs },
          initialRequestId,
        );
      }
      throw new QueryCancelledError({ path, method });
    }
    if (lastError) throw lastError;
    // Network or unexpected
    const message = e instanceof Error ? e.message : 'Network error';
    const err = new ServiceHttpError('INTERNAL', message, undefined, undefined);
    throw err;
  }
}

export async function get<T>(path: string, init: Omit<RequestInit, 'method'> & { ctx?: RequestContext; retry?: RetryOptions; timeoutMs?: number } = {}) {
  return serviceFetch<T>(path, { ...init, method: 'GET' });
}

export async function post<T>(path: string, body?: unknown, init: Omit<RequestInit, 'method' | 'body'> & { ctx?: RequestContext; retry?: RetryOptions; timeoutMs?: number } = {}) {
  return serviceFetch<T>(path, { ...init, method: 'POST', body: encodeBody(body) });
}

export async function put<T>(path: string, body?: unknown, init: Omit<RequestInit, 'method' | 'body'> & { ctx?: RequestContext; retry?: RetryOptions; timeoutMs?: number } = {}) {
  return serviceFetch<T>(path, { ...init, method: 'PUT', body: encodeBody(body) });
}

export async function patch<T>(path: string, body?: unknown, init: Omit<RequestInit, 'method' | 'body'> & { ctx?: RequestContext; retry?: RetryOptions; timeoutMs?: number } = {}) {
  return serviceFetch<T>(path, { ...init, method: 'PATCH', body: encodeBody(body) });
}

export async function del<T>(path: string, init: Omit<RequestInit, 'method'> & { ctx?: RequestContext; retry?: RetryOptions; timeoutMs?: number } = {}) {
  return serviceFetch<T>(path, { ...init, method: 'DELETE' });
}

// Token management is now handled server-side via HttpOnly cookies
// These functions are kept for backward compatibility but return null
// They may be removed in a future cleanup

/**
 * @deprecated Token management is now server-side via HttpOnly cookies
 * Returns null - tokens are managed by Service Layer in cookies
 */
export async function getAuthToken(): Promise<string | null> {
  // Test hooks: allow overriding in tests via window.__TEST_TOKEN__
  const testToken = typeof window !== 'undefined' ? (window as Window & { __TEST_TOKEN__?: string }).__TEST_TOKEN__ : undefined;
  if (testToken) {
    return testToken;
  }
  // No token from localStorage - using cookie-based auth
  return null;
}

